"use client";

import React from "react";
import { ArrowRight, CircleCheck as CheckCircle2, Clock, PhoneCall, ShieldCheck, Wrench, Sparkles } from 'lucide-react';
import { motion, useReducedMotion } from "framer-motion";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent } from "@/components/ui/card";
const features = [{
  title: "Genuine Delta Parts",
  description: "Factory-grade cartridges, seals, and spray heads for exact repairs."
}, {
  title: "24/7 Fast Response",
  description: "Emergency leak diagnosis and sink repairs across Jonesboro, GA."
}, {
  title: "Licensed Technicians",
  description: "Certified plumbing specialists skilled in all Delta kitchen and bath lines."
}];
export default function DeltaRepair() {
  const shouldReduceMotion = useReducedMotion();
  const containerVariants = {
    hidden: {
      opacity: shouldReduceMotion ? 1 : 0
    },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: shouldReduceMotion ? 0 : 0.12,
        delayChildren: 0.1
      }
    }
  };
  const itemVariants = {
    hidden: {
      opacity: shouldReduceMotion ? 1 : 0,
      y: shouldReduceMotion ? 0 : 16
    },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.5,
        ease: "easeOut"
      }
    }
  };
  return <section id="herodeltarepair" data-nav="light" className="relative overflow-hidden bg-background py-16 md:py-24 lg:py-28 text-foreground">
      {/* Subtle Background Elements */}
      <div className="pointer-events-none absolute inset-0 z-0 overflow-hidden">
        <div className="absolute -top-40 right-0 h-96 w-96 rounded-full bg-primary/5 blur-3xl" />
        <div className="absolute top-1/2 -left-32 h-80 w-80 rounded-full bg-accent/10 blur-3xl" />
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_bottom_left,var(--color-secondary)_0%,transparent_50%)] opacity-30" />
      </div>

      <div className="container relative z-10 mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 items-center gap-12 lg:grid-cols-12 lg:gap-8">
          {/* Left Column: Brand Copy & CTAs */}
          <motion.div variants={containerVariants} initial="hidden" animate="visible" className="flex flex-col gap-6 lg:col-span-7">
            <motion.div variants={itemVariants} className="flex flex-wrap items-center gap-2">
              <Badge variant="secondary" className="px-3 py-1 text-xs font-semibold uppercase tracking-wide">
                <Sparkles className="size-3.5 text-accent" />
                Delta Faucet & Sink Specialists
              </Badge>
              <span className="text-xs font-medium text-muted-foreground">Jonesboro & Clayton County</span>
            </motion.div>

            <motion.div variants={itemVariants} className="space-y-4">
              <h1 className="font-default text-3xl font-bold tracking-tight text-foreground sm:text-4xl md:text-5xl lg:text-5xl">
                Expert Delta Faucet & Sink Repair in Jonesboro
              </h1>
              <p className="max-w-2xl text-base text-muted-foreground sm:text-lg">
                Stop drips, low pressure, and fixture leaks with local plumbing specialists trained on genuine Delta technologies. We bring fast, dependable repairs and OEM parts directly to your door.
              </p>
            </motion.div>

            {/* CTA Group */}
            <motion.div variants={itemVariants} className="flex flex-wrap items-center gap-4 pt-2">
              <Button asChild size="lg" className="shadow-md">
                <Link to="/contact/" className="inline-flex items-center gap-2">
                  <span>Request Delta Service</span>
                  <ArrowRight className="size-4" />
                </Link>
              </Button>
              <Button asChild variant="outline" size="lg">
                <Link to="/service-areas/">
                  <span>View Service Areas</span>
                </Link>
              </Button>
              <Button asChild variant="ghost" size="lg" className="border border-border">
                <a href="tel:4708479076" className="inline-flex items-center gap-2 text-foreground">
                  <PhoneCall className="size-4 text-primary" />
                  <span className="font-semibold">Call 24/7 Support</span>
                </a>
              </Button>
            </motion.div>

            {/* Quick Guarantees / Badges */}
            <motion.div variants={itemVariants} className="mt-6 grid grid-cols-1 gap-4 border-t border-border pt-6 sm:grid-cols-3">
              {features.map((feature, index) => <div key={feature.title} data-index={index} className="flex flex-col gap-1.5">
                  <div className="flex items-center gap-2 text-foreground font-semibold text-sm">
                    <CheckCircle2 className="size-4 text-chart-4 shrink-0" />
                    <span>{feature.title}</span>
                  </div>
                  <p className="text-xs text-muted-foreground leading-relaxed">
                    {feature.description}
                  </p>
                </div>)}
            </motion.div>
          </motion.div>

          {/* Right Column: Visual Showcase & Trust Card */}
          <motion.div initial={{
          opacity: shouldReduceMotion ? 1 : 0,
          scale: shouldReduceMotion ? 1 : 0.98
        }} animate={{
          opacity: 1,
          scale: 1
        }} transition={{
          duration: 0.6,
          ease: "easeOut",
          delay: 0.2
        }} className="lg:col-span-5">
            <div className="relative mx-auto max-w-md lg:max-w-none">
              {/* Outer Card with Main Visual */}
              <div className="relative overflow-hidden rounded-2xl border border-border bg-card shadow-xl">
                <div className="relative aspect-[4/3] w-full overflow-hidden bg-muted">
                  <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1778865/img/delta_faucet_repair_hero.png" data-wvc-srcset="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1778865/img/delta_faucet_repair_hero.png-wvc-srcset" data-wvc-sizes="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1778865/img/delta_faucet_repair_hero.png-wvc-sizes" alt="Plumber repairing modern Delta kitchen faucet and sink fixture" loading="lazy" decoding="async" className="h-full w-full object-cover" />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent" />
                  
                  {/* Floating badge inside image */}
                  <div className="absolute bottom-4 left-4 right-4 flex items-center justify-between text-white">
                    <div>
                      <p className="text-xs font-semibold uppercase tracking-wider text-accent">Brand Expertise</p>
                      <p className="text-sm font-bold">Delta Diamond Seal & Touch2O</p>
                    </div>
                    <Badge variant="default" className="bg-primary text-primary-foreground">
                      Certified Pro
                    </Badge>
                  </div>
                </div>

                {/* Sub-card with Diagnostic highlight */}
                <div className="p-6">
                  <div className="flex items-start gap-4">
                    <div className="rounded-lg bg-secondary p-3 text-secondary-foreground shrink-0">
                      <Wrench className="size-6 text-primary" />
                    </div>
                    <div>
                      <h2 className="font-default text-lg font-semibold text-card-foreground">
                        Comprehensive Delta Diagnostics
                      </h2>
                      <p className="mt-1 text-sm text-muted-foreground">
                        From leaky base rings and Touch2O solenoid valve glitches to complete basin replacements, we restore full water efficiency quickly.
                      </p>
                    </div>
                  </div>

                  <div className="mt-6 flex items-center justify-between border-t border-border pt-4 text-xs text-muted-foreground">
                    <div className="flex items-center gap-1.5">
                      <Clock className="size-4 text-primary" />
                      <span>Same-day appointments</span>
                    </div>
                    <div className="flex items-center gap-1.5">
                      <ShieldCheck className="size-4 text-chart-4" />
                      <span>Parts & Labor Warranty</span>
                    </div>
                  </div>
                </div>
              </div>

              {/* Decorative Accent Card Floating */}
              <div className="absolute -bottom-5 -left-5 hidden sm:block">
                <Card className="border-border bg-popover/95 p-3 shadow-lg backdrop-blur-sm">
                  <CardContent className="p-0 flex items-center gap-3">
                    <div className="flex size-10 items-center justify-center rounded-full bg-accent text-accent-foreground font-bold text-sm">
                      24h
                    </div>
                    <div className="text-xs">
                      <p className="font-semibold text-popover-foreground">Emergency Plumbing</p>
                      <p className="text-muted-foreground">Jonesboro local dispatch</p>
                    </div>
                  </CardContent>
                </Card>
              </div>
            </div>
          </motion.div>
        </div>
      </div>
    </section>;
}